/**
 * kbrainObservationService — KBRAIN-LOG-1 client-side corpus logging substrate.
 *
 * Called by all three governed wrappers (governedAegisCall, governedArtemisCall,
 * governedCouncilCall) after every successful inference call to record a
 * structured observation tuple in the KBRAIN corpus.
 *
 * Architecture — two-phase write:
 *   Phase 1 (CF): kbrainLogObservation Cloud Function writes the full input
 *     context to GCS cold storage and returns { inputContextHash, inputContextRef }.
 *     FAIL-CLOSED: if GCS write fails, the CF returns an error and no Firestore
 *     write occurs. The observation is dropped for that call.
 *   Phase 2 (DataService): kbrainObservationService writes the structured
 *     observation doc to `kbrain_observations` via DataService (the only
 *     sanctioned Firestore write path per architecture rules).
 *   Phase 3 (EventBus): emits `kbrain.observation.recorded` for downstream
 *     consumers (KBRAIN-TAX-1 will subscribe; none in this session).
 *
 * Callers fire-and-forget this service — it logs errors internally and never
 * throws. Observation loss does not block the governed inference call.
 *
 * Session: KBRAIN-LOG-1 (Wave KB-A, Seat 2)
 * Patent gate: ELYON-IDM-KBRAIN-001, ELYON-IDM-KBRAIN-002 (filed pending)
 */

import DataService from '../DataService.js';
import EventBusService from '../EventBusService.js';

// CF endpoint resolved dynamically at call time so tests can use vi.stubEnv()
// without requiring vi.resetModules() + dynamic re-import.
function _getCfUrl() {
  return (typeof import.meta !== 'undefined' && import.meta.env?.VITE_KBRAIN_LOG_OBSERVATION_URL) || null;
}

const KBRAIN_COLLECTION = 'kbrain_observations';

/**
 * Best-effort PII detection. Dynamically imports the platform PII scanner
 * when available; returns false (not detected) on any import or scan failure.
 * Result is passed to the CF so it can set corpusEligible correctly.
 *
 * @param {*} inputContext
 * @returns {Promise<boolean>}
 */
async function _detectPii(inputContext) {
  try {
    const { scanForPii } = await import('../obs/piiScanner.js');
    const result = await scanForPii(inputContext);
    return result?.detected === true;
  } catch {
    // PII scanner unavailable — default false. Corpus will include this call
    // unless tenant is opted out. KBRAIN-SEC-1 will harden this path.
    return false;
  }
}

/**
 * Call the GCS cold-storage CF. Returns { inputContextHash, inputContextRef }
 * on success, or null on failure (caller treats null as fail-closed: no Firestore write).
 *
 * @param {object} payload — body to send to the CF
 * @returns {Promise<{inputContextHash: string, inputContextRef: string}|null>}
 */
async function _offloadToGcs(payload) {
  const cfUrl = _getCfUrl();
  if (!cfUrl) {
    // Dev/test mode: no GCS offload. Return synthetic refs so the observation
    // can still be written to Firestore (localStorage fallback) for local dev.
    const hash = `dev_${Date.now().toString(36)}`;
    return { inputContextHash: hash, inputContextRef: `local://${hash}` };
  }

  try {
    const res = await fetch(cfUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const errText = await res.text().catch(() => 'unknown');
      throw new Error(`kbrainLogObservation CF ${res.status}: ${errText}`);
    }
    const data = await res.json();
    return {
      inputContextHash:   data.inputContextHash,
      inputContextRef:    data.inputContextRef,
      corpusEligible:     data.corpusEligible,      // authoritative from CF (checks tenant opt-out + PII)
      corpusOptOutReason: data.corpusOptOutReason,  // authoritative from CF
    };
  } catch (err) {
    // Fail-closed: GCS write failed → caller must not write to Firestore.
    console.error('[kbrainObservationService] GCS offload failed (fail-closed — observation dropped):', err.message);
    return null;
  }
}

const kbrainObservationService = {
  /**
   * Log a structured observation tuple for one governed inference call.
   *
   * Fire-and-forget from caller perspective: never throws.
   * Returns { observationId } on success, null on any failure.
   *
   * @param {object} opts
   * @param {string}        opts.tenantId
   * @param {string}        opts.capabilityNamespace  — e.g. 'aegis', 'comms', 'kfol'
   * @param {string}        [opts.callTaxonomy]       — default 'uncategorized'; KBRAIN-TAX-1 fills this
   * @param {string}        opts.modelFamily           — 'anthropic'|'openai'|'google'|'self-hosted-llama'|'self-hosted-qwen'
   * @param {string}        [opts.modelId]             — e.g. 'claude-haiku-4-5'
   * @param {object|string} [opts.inputContext]        — arbitrary call context (offloaded to GCS)
   * @param {object}        [opts.modelOutput]         — structured output (stored in Firestore)
   * @param {string}        [opts.modelOutputRaw]      — raw text output (stored in Firestore; optional)
   * @param {string}        [opts.correlationId]       — x-correlation-id (internal only)
   * @param {boolean}       [opts.isHighStakes]        — derived from materialityBand
   * @param {string}        [opts.materialityBand]     — 'ROUTINE'|'ELEVATED'|'MATERIAL'|'EXISTENTIAL'
   * @returns {Promise<{observationId: string}|null>}
   */
  async log(opts) {
    const {
      tenantId,
      capabilityNamespace,
      callTaxonomy,
      modelFamily,
      modelId,
      inputContext,
      modelOutput,
      modelOutputRaw,
      correlationId,
      isHighStakes,
      materialityBand,
    } = opts || {};

    // Defensive: tenantId is mandatory — without it we cannot enforce tenant isolation.
    if (!tenantId) {
      console.warn('[kbrainObservationService] log() called without tenantId — skipping');
      return null;
    }

    // Phase 0: best-effort PII scan (flag passed to CF for corpusEligible logic)
    const piiDetected = await _detectPii(inputContext);

    // Phase 1: GCS cold-storage offload — fail-closed
    const gcsResult = await _offloadToGcs({
      tenantId,
      capabilityNamespace: capabilityNamespace || 'uncategorized',
      inputContext: inputContext || null,
      piiDetected,
      callTimestamp: new Date().toISOString(),
    });

    if (!gcsResult) {
      // GCS failed — do not write to Firestore (fail-closed doctrine).
      return null;
    }

    const { inputContextHash, inputContextRef } = gcsResult;
    const band = materialityBand || 'ROUTINE';

    // Phase 2: Firestore write via DataService (only sanctioned write path)
    let created;
    try {
      created = await DataService.create(
        KBRAIN_COLLECTION,
        {
          tenantId,
          capabilityNamespace: capabilityNamespace || 'uncategorized',
          callTaxonomy: callTaxonomy || 'uncategorized',
          modelFamily: modelFamily || 'anthropic',
          modelId: modelId || null,
          inputContextHash,
          inputContextRef,
          modelOutput: modelOutput || null,
          modelOutputRaw: modelOutputRaw || null,
          actionTaken: null,
          correlationId: correlationId || null,
          isHighStakes: isHighStakes === true,
          materialityBand: band,
          // Ground-truth fields — backfilled by scheduled CFs (24h/48h/72h)
          groundTruth24h: null,
          groundTruth48h: null,
          groundTruth72h: null,
          outcomePolarity24h: null,
          outcomePolarity48h: null,
          outcomePolarity72h: null,
          groundTruthSource24h: 'not-yet-joined',
          groundTruthSource48h: 'not-yet-joined',
          groundTruthSource72h: 'not-yet-joined',
          groundTruthJoinedAt24h: null,
          groundTruthJoinedAt48h: null,
          groundTruthJoinedAt72h: null,
          // Corpus eligibility — authoritative value comes from the CF (which
          // checks both tenant opt-out and piiDetected flag). Client-side
          // piiDetected is an advisory hint sent to the CF, not the source of truth.
          corpusEligible: gcsResult.corpusEligible !== undefined ? gcsResult.corpusEligible : !piiDetected,
          corpusOptOutReason: gcsResult.corpusOptOutReason || (piiDetected ? 'pii-detected' : null) || null,
          softDeleted: false,
        },
        'kbrain-log-service',
        'kbrain-observation'
      );
    } catch (err) {
      console.error('[kbrainObservationService] Firestore write failed:', err.message);
      return null;
    }

    const observationId = created?.id || created?.observationId || null;

    // Phase 3: EventBus — kbrain.observation.recorded
    // Non-fatal: EventBus failure does not roll back the observation.
    EventBusService.publish({
      eventType: 'kbrain.observation.recorded',
      tenantId,
      sourceModule: 'kbrain',
      sourceId: observationId || 'unknown',
      payload: {
        observationId,
        tenantId,
        capabilityNamespace: capabilityNamespace || 'uncategorized',
        materialityBand: band,
        callTimestamp: new Date().toISOString(),
      },
      actor: 'kbrain-log-service',
    }).catch(err => {
      console.warn('[kbrainObservationService] EventBus publish failed (non-fatal):', err.message);
    });

    return { observationId };
  },
};

export default kbrainObservationService;
